/**
 * Contact Method Utilities
 *
 * Shared functions for validating and formatting contact methods (email, phone).
 */

/**
 * Check if a string contains any letters
 */
export function hasLetters(value: string): boolean {
  return /[a-zA-Z]/.test(value);
}

/**
 * Get digit count from a string
 */
export function getDigitCount(value: string): number {
  return (value.match(/\d/g) || []).length;
}

/**
 * Check if a contact method string is an email address.
 * Simple heuristic: contains '@'.
 */
export function isEmail(value: string): boolean {
  return value.includes('@');
}

/**
 * Determine validation type based on input.
 * Returns 'email' if contains @, 'phone' if 7+ digits and no letters, null otherwise.
 */
export function getValidationType(value: string): 'email' | 'phone' | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (trimmed.includes('@')) return 'email';
  if (hasLetters(trimmed)) return null;
  if (getDigitCount(trimmed) >= 7) return 'phone';

  return null;
}

/**
 * Split a raw phone input into a base number and an optional extension.
 *
 * Bug-hunt #76: `formatPhone(+1 555-1234 x42)` used to fold `x42` into
 * the digit count, producing a nonsense 10-digit US number. The
 * extension separator is conventionally any of `x`, `X`, `ext`, `ext.`,
 * `extension` (case-insensitive).
 */
const EXTENSION_SPLIT = /[xX]\.?|ext\.?|extension/i;

export interface PhoneParts {
  base: string;
  extension: string | null;
}

export function splitPhone(value: string): PhoneParts {
  const match = value.search(EXTENSION_SPLIT);
  if (match < 0) return { base: value, extension: null };

  const before = value.slice(0, match);
  // Skip past the separator characters until we hit a digit; everything
  // after is the extension.
  const afterStart = value.slice(match);
  const extStart = afterStart.search(/\d/);
  if (extStart < 0) {
    return { base: before, extension: null };
  }
  // F10: extension contract is "digits only". Pick the first contiguous
  // digit run; anything after (alpha garbage, second separator) is junk.
  const digits = (afterStart.slice(extStart).match(/^\d+/) ?? [''])[0];
  return {
    base: before,
    extension: digits,
  };
}

/**
 * Format phone number for display.
 * Handles 10-digit (US) and 11-digit (+1) phone numbers.
 *
 * Bug-hunt #76: extensions (x42, ext. 5) are preserved as a suffix
 * rather than silently folded into the base-number digits.
 */
export function formatPhone(value: string): string {
  const { base, extension } = splitPhone(value);
  const digits = base.replace(/\D/g, '');
  const ext = extension ? ` x${extension}` : '';
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}${ext}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}${ext}`;
  }
  return value;
}

/**
 * Format a "default email or phone" string for display. Emails pass through
 * unchanged; anything else routes through formatPhone. Single entry point so
 * the same value renders identically everywhere (detail dialog, tables, etc).
 */
export function formatContactMethod(value: string): string {
  return isEmail(value) ? value : formatPhone(value);
}

/**
 * Canonical identity key for a contact method — the thing itself, not its
 * spelling.
 *
 * The server owns normalization (vingapi `normalize_email` / `format_e164`),
 * so the value that comes back is routinely spelled differently from the one
 * the user typed: `5551234567` is stored and returned as `+15551234567`. Any
 * client-side "is this the same method?" comparison must therefore key on
 * this, never on string equality — comparing raw values is what left a newly
 * added phone rendered twice until a reload (2026-09-01).
 *
 * Emails fold case; phones reduce to digits, dropping a leading US country
 * code exactly where `formatPhone` does (11 digits starting with 1), and keep
 * an extension as a distinct suffix so `555-1234 x1` and `555-1234 x2` stay
 * different methods. Anything that is neither is returned trimmed, so an
 * unrecognized value is only ever equal to itself.
 */
export function contactMethodKey(value: string): string {
  const trimmed = value.trim();
  if (isEmail(trimmed)) return trimmed.toLowerCase();

  const { base, extension } = splitPhone(trimmed);
  let digits = base.replace(/\D/g, '');
  if (!digits) return trimmed.toLowerCase();
  if (digits.length === 11 && digits[0] === '1') digits = digits.slice(1);
  return extension ? `${digits}x${extension}` : digits;
}

/** Whether two contact methods are the same method, however each is spelled. */
export function isSameContactMethod(a: string, b: string): boolean {
  return contactMethodKey(a) === contactMethodKey(b);
}
